// @ts-nocheck
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_COLORS as C, APP_FONT as F } from '../Dashboard/tokens';
import { fmtMoney } from '../Dashboard/helpers';
import { useDashboardData } from '../Dashboard/useDashboardData';
import CashFlowCard from '../Dashboard/components/CashFlowCard';

/**
 * Cash flow overview — Reports page.
 * Reuses CashFlowCard from Dashboard, adds AllAccountsCard + ThisMonthsFlow + InsightsGrid.
 */
export default function CashFlowOverview(): React.ReactElement {
  const navigate = useNavigate();
  const { data: dash, isLoading } = useDashboardData();

  // Guard against undefined data (initial render, API error, or missing fields).
  // Earlier the loading-only guard let d.bankAccounts.reduce crash on an undefined d,
  // producing a blank page in prod. Treat anything short of a full data object as "still loading".
  if (!dash) {
    return <div style={{ color: C.sub, fontSize: 13, padding: '40px 0', fontFamily: F.family }}>Loading cash flow overview...</div>;
  }
  const d = dash;
  const bankAccounts = Array.isArray(d.bankAccounts) ? d.bankAccounts : [];
  const cashFlow     = Array.isArray(d.cashFlow)     ? d.cashFlow     : [];
  const todayCashBalance = bankAccounts.reduce((s, a) => s + (Number(a && a.bankBalance) || 0), 0);

  return (
    <div style={{ fontFamily: F.family }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: '0 0 2px' }}>Cash flow overview</h2>
      <div style={{ fontSize: 11, color: C.sub, margin: '0 0 18px' }}>Updated just now · 12 months</div>

      {/* TOP ROW */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.7fr', gap: 14, marginBottom: 18 }}>
        <AllAccountsCard bankAccounts={bankAccounts} total={todayCashBalance} />
        <CashFlowCard
          points={cashFlow}
          todayCashBalance={todayCashBalance}
          onViewDetail={() => navigate('/reports/planning/cash-flow-planner')}
        />
      </div>

      {/* THIS MONTH'S FLOW */}
      <ThisMonthsFlow dash={d} />

      {/* INSIGHTS */}
      <InsightsGrid />
    </div>
  );
}

/* ---------- ALL ACCOUNTS CARD ---------- */
function AllAccountsCard({ bankAccounts, total }: { bankAccounts: any[]; total: number }): React.ReactElement {
  return (
    <div style={{ background: '#fff', border: '1px solid ' + C.line, borderRadius: 14, padding: '16px 18px' }}>
      <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: '#475569', marginBottom: 6 }}>All accounts</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: C.ink, letterSpacing: '-0.015em' }}>{fmtMoney(total)}</div>
      <div style={{ fontSize: 10, color: C.sub, marginTop: 2 }}>Today's cash balance</div>

      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid ' + C.line, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700, color: '#475569' }}>
          <span>Bank accounts</span>
          <span style={{ color: C.ink }}>{fmtMoney(total)}</span>
        </div>
        {bankAccounts.map((a, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: i < bankAccounts.length - 1 ? '1px solid ' + C.line : 'none', fontSize: 11 }}>
            <span style={{ color: C.sub }}>{a.name || a.bankName || 'Account'}</span>
            <span style={{ color: C.ink, fontWeight: 700 }}>{fmtMoney(a.bankBalance)}</span>
          </div>
        ))}
      </div>

      <div style={{ color: C.tealInk, fontSize: 11, fontWeight: 600, marginTop: 10, cursor: 'pointer' }}>+ Link another account</div>
    </div>
  );
}

/* ---------- THIS MONTH'S FLOW ---------- */
function ThisMonthsFlow({ dash }: { dash: any }): React.ReactElement {
  // Derive this month's numbers from dash data where possible
  const monthIn  = 3937;  // TODO wire to real backend
  const monthOut = 0;
  const net = monthIn - monthOut;
  const overdueInvoices = 13;
  const overdueAmount   = 3937;
  const openInvoices    = 0;
  const paidThisMonth   = 0;
  const overdueBills    = 0;
  const openBills       = 0;
  const paidBills       = 0;

  const now = new Date();
  const monthName = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const daysLeft  = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate() - now.getDate();

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '6px 0 10px' }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, color: C.ink, margin: 0 }}>This month's flow</h3>
        <div style={{ fontSize: 11, color: C.sub }}>{monthName} · {daysLeft} days remaining</div>
      </div>

      <div style={{ background: '#fff', border: '1px solid ' + C.line, borderRadius: 14, padding: '18px 20px', marginBottom: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 24, alignItems: 'center' }}>
          <FlowCol color="#0F6E56" label="Money in" amount={monthIn} rows={[
            { label: 'Overdue invoices (' + overdueInvoices + ')', amount: overdueAmount, urgent: true },
            { label: 'Open invoices (' + openInvoices + ')', amount: 0 },
            { label: 'Paid this month', amount: paidThisMonth },
          ]}/>
          <div style={{ textAlign: 'center', minWidth: 100 }}>
            <div style={{ fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 800, color: '#475569', marginBottom: 4 }}>Net</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: net >= 0 ? '#0F6E56' : '#DC2626' }}>{net >= 0 ? '+' : ''}{fmtMoney(net)}</div>
            <div style={{ fontSize: 10, color: C.sub, marginTop: 2 }}>if all collected</div>
          </div>
          <FlowCol color="#DC2626" label="Money out" amount={monthOut} rows={[
            { label: 'Overdue bills (' + overdueBills + ')', amount: 0 },
            { label: 'Open bills (' + openBills + ')', amount: 0 },
            { label: 'Paid this month', amount: paidBills },
          ]}/>
        </div>

        {/* Action pills */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid ' + C.line, flexWrap: 'wrap' }}>
          <ActionPill vela>✨ Vela drafted 13 reminders <span style={{ fontSize: 10, background: 'rgba(255,255,255,0.2)', padding: '2px 6px', borderRadius: 999, fontWeight: 700 }}>Review</span></ActionPill>
          <ActionPill>+ Create invoice</ActionPill>
          <ActionPill>+ Create bill</ActionPill>
          <ActionPill>Record payment</ActionPill>
        </div>
      </div>
    </>
  );
}

function FlowCol({ color, label, amount, rows }: { color: string; label: string; amount: number; rows: Array<{ label: string; amount: number; urgent?: boolean }> }): React.ReactElement {
  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6, color }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }}/>{label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: '-0.015em', marginBottom: 8 }}>{fmtMoney(amount)}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: C.sub }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{r.label}</span>
            <span style={{ color: r.urgent ? '#DC2626' : C.ink, fontWeight: 700 }}>{fmtMoney(r.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActionPill({ children, vela }: { children: React.ReactNode; vela?: boolean }): React.ReactElement {
  const style: React.CSSProperties = vela
    ? { background: 'linear-gradient(135deg, #8B5CF6 0%, #6B46C1 100%)', color: '#fff', border: 'none' }
    : { background: '#F7F8FA', color: C.ink, border: '1px solid ' + C.line };
  return (
    <button style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11.5, fontWeight: 600, padding: '7px 12px', borderRadius: 999, cursor: 'pointer', ...style }}>
      {children}
    </button>
  );
}

/* ---------- INSIGHTS GRID ---------- */
function InsightsGrid(): React.ReactElement {
  const insights = [
    { kind: 'urgent', badge: 'Overdue',   time: 'now',       body: <>13 invoices overdue totalling <b>$3,937</b>. Longest: Alberta Health Services, 61 days.</>, btn: 'Send 13 reminders', ghost: false },
    { kind: 'vela',   badge: '✨ Vela draft', time: '10 min ago', body: <>Cash balance above threshold for <b>14 days</b>. Consider moving <b>$1,000</b> to high-interest savings.</>, btn: 'Draft transfer', ghost: false },
    { kind: 'warn',   badge: 'Trend',     time: 'today',     body: <>Expenses tracking <b>+12%</b> higher than last month. Top driver: fuel &amp; mileage.</>, btn: 'View breakdown', ghost: true },
    { kind: 'good',   badge: 'On track',  time: 'today',     body: <>Payroll run due in <b>3 days</b> ($14,284 for 8 employees). All timesheets approved.</>, btn: 'Review run', ghost: true },
  ];
  const edgeColor: Record<string, string> = { urgent: '#DC2626', vela: '#8B5CF6', warn: '#CA8A04', good: '#0F6E56' };
  const badgeStyle: Record<string, React.CSSProperties> = {
    urgent: { background: '#FEE2E2', color: '#DC2626' },
    vela:   { background: 'rgba(139,92,246,0.12)', color: '#8B5CF6' },
    warn:   { background: '#FEF3C7', color: '#CA8A04' },
    good:   { background: '#DCFCE7', color: '#0F6E56' },
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '6px 0 10px' }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, color: C.ink, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>✨ Insights &amp; ideas</h3>
        <div style={{ fontSize: 11, color: C.sub }}>Updated by Vela just now</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        {insights.map((it, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid ' + C.line, borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: edgeColor[it.kind] }}/>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 999, letterSpacing: '0.08em', textTransform: 'uppercase', ...badgeStyle[it.kind] }}>{it.badge}</span>
              <span style={{ fontSize: 10, color: '#94A3B8', marginLeft: 'auto' }}>{it.time}</span>
            </div>
            <div style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.5 }}>{it.body}</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 }}>
              <button style={{ fontSize: 11, fontWeight: 700, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', background: it.ghost ? '#fff' : C.ink, color: it.ghost ? C.ink : '#fff', border: it.ghost ? '1px solid ' + C.line : 'none' }}>{it.btn}</button>
              <span style={{ fontSize: 10, color: '#94A3B8', cursor: 'pointer' }}>Dismiss</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
